import React, {useEffect, useState} from 'react'
import {profileDetailsInitValues as initialValues} from '../core/_models'
import {useFormik} from 'formik'
import {IUserProfile} from '../../../coreGlobal/models/user'
import {phoneMask} from '../../../utils/phoneMask'
import {cpfMask} from '../../../utils/cpfMask'
import {confirmProfileDetailsSchema} from '../validate/confirmProfileDetailsSchema'
import Swal from 'sweetalert2'
import {putProfilePhoto} from '../core/_requests'
import {useNavigate} from 'react-router-dom'

type Props = {
  userProfile: IUserProfile
}

const ProfileDetails: React.FC<Props> = ({userProfile}) => {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const formik = useFormik<IUserProfile>({
    initialValues,
    validationSchema: confirmProfileDetailsSchema,
    onSubmit: (values) => {
      submit(values)
    },
  })

  useEffect(() => {
    formik.setValues(userProfile)
  }, [userProfile])

  const submit = async (req: IUserProfile) => {
    await Swal.fire({
      title: 'Deseja atualizar os dados?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true)
        try {
          const data = await putProfilePhoto(req)
          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Salvo com sucesso',
            showConfirmButton: false,
            timer: 1500,
          })

          navigate(0)
        } catch (error: any) {
          await Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response.data.message,
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Detalhes do perfil</h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div className='card-body border-top p-9'>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Nome Completo</label>

            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                placeholder='Nome Completo'
                {...formik.getFieldProps('name')}
              />
              {formik.errors.name && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.name}</div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-bold fs-6'>CPF</label>

            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                placeholder='***.***.***-**'
                value={cpfMask(formik.values.cpf)}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/\D/g, '')

                  const limited = onlyNumbers.slice(0, 11)

                  const masked = cpfMask(limited)

                  formik.setFieldValue('cpf', masked)
                }}
              />
              {formik.errors.cpf && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.cpf}</div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Celular</span>
            </label>

            <div className='col-lg-8 fv-row'>
              <input
                type='tel'
                className='form-control form-control-lg form-control-solid'
                placeholder='Número de telefone'
                value={phoneMask(formik.values.phone)}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/\D/g, '')

                  const limited = onlyNumbers.slice(0, 11)

                  const masked = phoneMask(limited)

                  formik.setFieldValue('phone', masked)
                }}
              />
              {formik.errors.phone && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.phone}</div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Comunicação</span>
            </label>

            <div className='col-lg-8 fv-row'>
              <div className='d-flex align-items-center mt-3'>
                <label className='form-check form-check-inline form-check-solid me-5'>
                  <input
                    className='form-check-input'
                    name='communication[]'
                    type='checkbox'
                    checked={formik.values.notification_preference === 1}
                    onChange={() => formik.setFieldValue('notification_preference', 1)}
                  />
                  <span className='fw-bold ps-2 fs-6'>E-mail</span>
                </label>

                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    name='communication[]'
                    type='checkbox'
                    checked={formik.values.notification_preference === 2}
                    onChange={() => formik.setFieldValue('notification_preference', 2)}
                  />
                  <span className='fw-bold ps-2 fs-6'>Telefone</span>
                </label>

                <label className='form-check form-check-inline form-check-solid'>
                  <input
                    className='form-check-input'
                    name='communication[]'
                    type='checkbox'
                    checked={formik.values.notification_preference === 3}
                    onChange={() => formik.setFieldValue('notification_preference', 3)}
                  />
                  <span className='fw-bold ps-2 fs-6'>E-mail e Telefone</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button type='submit' className='btn btn-danger' disabled={loading || !formik.isValid}>
            {!loading && 'Salvar Alterações'}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Por favor, aguarde...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export {ProfileDetails}
